import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Paper,
  Chip,
  Stack,
  useTheme,
  alpha,
  Fade,
  Grid
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FiberNew as NewIcon,
  Schedule as ProgressIcon,
  PendingActions as PendingIcon,
  CheckCircle as ApprovedIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import Chart from 'react-apexcharts';
import { NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { apiCall } from "../../services/ApiCall.js";
import config from "../../config/config.json";

// Styled Components with #0d6efd theme
const StyledContainer = styled(Box)`
 
  border-radius: 24px;
  padding: 24px;
  
  position: relative;
  overflow: hidden;
  
`;

const ContentWrapper = styled(Box)`
  position: relative;
  z-index: 1;
`;

const StyledCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95) !important;
  
  border-radius: 20px !important;
  box-shadow: 0 8px 32px #00000026 !important;
  border: 1px solid rgba(13, 110, 253, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
`;

const DateNavigator = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
 
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  padding: 12px 20px;
  box-shadow: 0 4px 20px rgba(13, 110, 253, 0.1);
  border: 1px solid rgba(13, 110, 253, 0.08);
`;

const StyledNavButton = styled(IconButton)`
  background: #EDF4FF !important;
  color: black !important;
  width: 30px !important;
  height: 30px !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    transform: scale(1.1);
    color: white !important;
    box-shadow: 0 4px 15px rgba(13, 110, 253, 0.4) !important;
    background: linear-gradient(135deg, #0a58ca 0%, #084298 100%) !important;
  }
`;

const RequestItemCard = styled(Paper)`
  padding: 16px;
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid rgba(13, 110, 253, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(13, 110, 253, 0.2) !important;
    background: rgba(255, 255, 255, 1) !important;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.gradient};
  }
`;

const StatusIndicator = styled(Box)`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color};
  box-shadow: 0 0 0 3px ${props => alpha(props.color, 0.2)};
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 ${props => alpha(props.color, 0.7)};
    }
    70% {
      box-shadow: 0 0 0 6px ${props => alpha(props.color, 0)};
    }
    100% {
      box-shadow: 0 0 0 0 ${props => alpha(props.color, 0)};
    }
  }
`;

const CircularChartComponent = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [requestData, setRequestData] = useState([]);
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [chartSeries, setChartSeries] = useState([0, 0, 0, 0]);
  

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const getRequestData = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getRequestGraphData`,
      {},
      {},
      "GET"
    );

    if (isSuccess) {
      const requestDatas = data?.data.reduce((acc, request) => {
        const { status, id, createdAt, updatedAt } = request;
        if (!acc[status]) {
          acc[status] = [];
        }
        acc[status].push({ id, createdAt, updatedAt });
        return acc;
      }, {});
      setRequestData(requestDatas);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    }
    getRequestData();
  }, []);

  useEffect(() => {
    if (requestData) {
      const newRequest = requestData[0] ? requestData[0].filter(item => {
        const itemDate = new Date(item.updatedAt);
        return itemDate.getMonth() === selectedMonth && itemDate.getFullYear() === currentYear;
      }).length : 0;

      const inRequest = requestData[1] ? requestData[1].filter(item => {
        const itemDate = new Date(item.updatedAt);
        return itemDate.getMonth() === selectedMonth && itemDate.getFullYear() === currentYear;
      }).length : 0;

      const completedRequest = requestData[2] ? requestData[2].filter(item => {
        const itemDate = new Date(item.updatedAt);
        return itemDate.getMonth() === selectedMonth && itemDate.getFullYear() === currentYear;
      }).length : 0;

      const approvedRequest = requestData[4] ? requestData[4].filter(item => {
        const itemDate = new Date(item.updatedAt);
        return itemDate.getMonth() === selectedMonth && itemDate.getFullYear() === currentYear;
      }).length : 0;

      setChartSeries([newRequest, inRequest, completedRequest, approvedRequest]);
    }
  }, [selectedMonth, requestData]);

  // Enhanced chart options with #0d6efd theme
  const chartOptions = {
    chart: {
      type: 'radialBar',
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    plotOptions: {
      radialBar: {
        hollow: {
          size: '70%',
          background: 'transparent',
        },
        track: {
          background: alpha('#e3f2fd', 0.5),
          strokeWidth: '100%',
          margin: 8,
        },
        dataLabels: {
          show: false,
        },
        inverseOrder: false,
        startAngle: 0,
        endAngle: 360
      },
    },
    colors: ['#0d6efd'],
    labels: ['New Requests'],
    stroke: {
      lineCap: 'round'
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'horizontal',
        shadeIntensity: 0.5,
        gradientToColors: ['#0a58ca'],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100]
      }
    }
  };

  const handlePreviousMonth = () => {
    setSelectedMonth(prevMonth => prevMonth === 0 ? 11 : prevMonth - 1);
  };

  const handleNextMonth = () => {
    setSelectedMonth(prevMonth => prevMonth === 11 ? 0 : prevMonth + 1);
  };

  const requestItems = [
    {
      to: "/new-request",
      label: "New Requests",
      count: chartSeries[0],
      color: "#E4F3FF",
      gradient: "linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)",
      icon: '/img/new-request.png'
    },
    {
      to: "/in-progress",
      label: "In-Progress",
      count: chartSeries[1],
      color: "#FFEDD5",
      gradient: "linear-gradient(135deg, #0a58ca 0%, #084298 100%)",
      icon: '/img/in-progress.png'
    },
    {
      to: "/to-be-approved",
      label: "To-Be-Approved",
      count: chartSeries[2],
      color: "#FFEBF1",
      gradient: "linear-gradient(135deg, #084298 0%, #052c65 100%)",
      icon: '/img/to-be-approved.png'
    },
    {
      to: "/approved-Request",
      label: "Approved",
      count: chartSeries[3],
      color: "#DCFCE7",
      gradient: "linear-gradient(135deg, #052c65 0%, #031633 100%)",
      icon: '/img/approved.png'
    }
  ];

  const totalRequests = chartSeries.reduce((sum, count) => sum + count, 0);

  return (
    <StyledContainer>
     
      <ContentWrapper>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: '#333333',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textAlign: 'left',
              mb: 1,
            }}
          >
            Request Status Analytics
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: '#64748b',
              textAlign: 'left'
            }}
          >
            Track and Analyze Requests
          </Typography>
          <Fade in timeout={500}>
            <DateNavigator sx={{ mb: 3 }}>
              <StyledNavButton onClick={handlePreviousMonth}>
                <ChevronLeftIcon />
              </StyledNavButton>


              <Typography
                
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#1a202c',
                  minWidth: '150px',
                  textAlign: 'center'
                }}
              >
                {`${monthNames[selectedMonth]}, ${currentYear}`}
              </Typography>

              <StyledNavButton onClick={handleNextMonth}>
                <ChevronRightIcon />
              </StyledNavButton>
            </DateNavigator>
          </Fade>
          </Box>
        </Box>

        {/* Date Navigator */}


        <Grid container spacing={3}>
          {/* Chart Section */}
          <Grid item xs={12} lg={6}>
            <StyledCard>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Chart
                    options={chartOptions}
                    series={[totalRequests]}
                    type="radialBar"
                    height={320}
                  />
                  {/* Center Content */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center'
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
                        backgroundClip: 'text',
                        textFillColor: 'transparent',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                      }}
                    >
                      {totalRequests}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontWeight: 500
                      }}
                    >
                      Total Requests
                    </Typography>
                  </Box>
                  <Typography
                      variant="h6"
                      sx={{
                        color: 'black',
                        fontWeight: 500
                      }}
                    >
                      Request Overview
                    </Typography>
                     <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontWeight: 500
                      }}
                    >
                      Current month analytics
                    </Typography>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Request Items */}
          <Grid item xs={12} lg={6}>
            <Stack spacing={3}>
              {requestItems.map((item, index) => (
                <Fade in timeout={500 + index * 100} key={item.to}>
                  <NavLink
                    to={item.to}
                    style={{ textDecoration: 'none' }}
                  >
                    <RequestItemCard >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Status Indicator */}
                        

                        {/* Icon */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: '12px',
                            background: item.color,
                            color: item.color
                          }}
                        >
                          <img src={item.icon} style={{ width: 24, height: 24 }}></img>
                        </Box>

                        {/* Content */}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: '#1a202c',
                              mb: 0.5
                            }}
                          >
                            {item.label}
                          </Typography>
                         
                        </Box>

                        {/* Count */}
                        <Chip
                          label={item.count}
                          sx={{
                            background: item.gradient,
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '14px',
                            minWidth: '50px'
                          }}
                        />
                      </Box>
                    </RequestItemCard>
                  </NavLink>
                </Fade>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </ContentWrapper>
    </StyledContainer>
  );
};

export default CircularChartComponent;